import { useState, useContext, useMemo, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { ClipboardCopyIcon, ExternalLinkIcon, XIcon } from "@heroicons/react/outline";
import { WorkflowContext } from "../contexts";
import { BLOCK_ICON_MAP, MIMETYPE_ICON_MAP } from "../utils";
import { ApprovalRequest, AbstractEmail, WorkflowAutomationItem, Attachment, TeamUser, SignatureRequest } from "../interfaces";
import { deleteData, getData, patchData, postData, putData } from "../backend";
import { useTranslation } from "react-i18next";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import useUser from "../hooks/useUser";
import useSWR from "swr";
import moment from "moment";
import useReviewItems from "../hooks/useReviewItems";
import EmailEditor, { AttachmentAction, Ref as EmailEditorRef } from "./EmailEditor";
import SignatureRequestEmailContant from "./SignatureRequestEmailContant";
import SignatureRequestLinkPreview from "./SignatureRequestLinkPreview";
import CopyToClipboard from "react-copy-to-clipboard";

export const APPROVAL_STATUS_MAP: Record<string, Array<string>> = {
  "PENDING": ["Pending", "border-gray-200 bg-gray-100 text-gray-600"],
  "REQUESTED": ["Requested", "border-blue bg-blue/10 text-blue"],
  "APPROVED": ["Approved", "border-green bg-green/10 text-green"],
  "REJECTED": ["Changes Requested", "border-red bg-red/10 text-red"],
  "CANCELED": ["Canceled", "border-red bg-red/10 text-red"],
}

export const CHANGE_REQUEST_STATUS_MAP: Record<string, Array<string>> = {
  "PENDING": ["Pending", "border-gray-200 bg-gray-100 text-gray-600"],
  "REQUESTED": ["Requested", "border-blue bg-blue/10 text-blue"],
  "APPROVED": ["Changes", "border-green bg-green/10 text-green"],
  "REJECTED": ["Rejcted", "border-red bg-red/10 text-red"],
  "CANCELED": ["Canceled", "border-red bg-red/10 text-red"],
}

function WorkflowOutputsDetail() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { t } = useTranslation();

  const { user, hasPermission } = useUser();

  const location = useLocation();
  const params = new URLSearchParams(location.search)

  const emailEditorRef = useRef<EmailEditorRef>(null);

  const itemId = params.get("item");

  const { data: currentItem, mutate } = useSWR<WorkflowAutomationItem>(itemId ? `/workflows/automation-items/${itemId}` : null, getData);

  const { id: workflowId } = useParams<{ id?: string }>();
  const { data: reviewItems, mutate: refreshReviewItems } = useReviewItems(workflowId);

  const { workflow, showAutomationPanel } = useContext(WorkflowContext);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);
  const [currentOutputGroup, setCurrentOutputGroup] = useState<"DOCUMENTS" | "EMAILS" | "SIGNATURE_REQUESTS">("DOCUMENTS");

  const [currentState, setCurrentState] = useState<"INITIAL" | "APPROVING" | "APPROVED" | "DELETING">("INITIAL");

  const [showRequestChanges, setShowRequestChanges] = useState(false);
  const [requestChangesMessage, setRequestChangesMessage] = useState<string>("The document(s) requires updates before approval can continue. Please review the suggested edits and resolve the comments.");

  const [copiedSignatureRequest, setCopiedSignatureRequest] = useState<string>();
  const [isSendingSequence, setIsSendingSequence] = useState(false);

  const latestApprovalRequest = currentItem?.approvalRequests.find((request) => request.status === "REQUESTED");
  const latestApprovalRequestForUser = currentItem?.approvalRequests.find((request) => request.status === "REQUESTED" && request.approver.id === user?.id);

  const canApprove = currentItem?.stage === "REVIEW_PENDING" && ((latestApprovalRequest?.id === latestApprovalRequestForUser?.id) || (!workflow.review?.approvalsInSequence && !!latestApprovalRequestForUser) || currentItem?.approvalRequests.length === 0);

  const sendableSignatureRequets = currentItem?.signatureRequests.filter(isSendable);
  const linkableSignatureRequets = currentItem?.signatureRequests.filter(sr => !isSendable(sr));

  function handleOnCopiedSignatureRequest(id: string) {
    setCopiedSignatureRequest(id);
    setTimeout(() => setCopiedSignatureRequest(undefined), 2000);
  }

  function isSendable(sr: SignatureRequest) {
    return !["PREVIEW", "LINK", "LINK_UNPUBLISHED"].includes(sr.status);
  }

  useEffect(() => {
    setCurrentOutputIndex(0);
    setCurrentOutputGroup("DOCUMENTS");
  }, [itemId]);

  useEffect(() => {
    setCurrentState("INITIAL");
    setShowRequestChanges(false);
    refreshReviewItems();
  }, [itemId, refreshReviewItems]);


  function isReviewable(item: WorkflowAutomationItem): boolean {
    return item.stage === "REVIEW_PENDING" && ((latestApprovalRequest?.id === latestApprovalRequestForUser?.id) || (!workflow.review?.approvalsInSequence && !!latestApprovalRequestForUser) || item.approvalRequests.length === 0);
  }

  const approversList: Array<ApprovalRequest> = useMemo(() => {
    if (!currentItem?.approvalRequests)
      return [];

    let result: Array<ApprovalRequest> = [];
    for (const approvalRequest of currentItem.approvalRequests) {
      const index = result.findIndex(a => approvalRequest.approver.id === a.approver.id);
      if (index >= 0)
        result[index] = approvalRequest.status !== "PENDING" ? approvalRequest : result[index];
      else
        result.push(approvalRequest);
    }

    return result;
  }, [currentItem?.approvalRequests]);

  function setCurrentItemOutput(outputGroup: "DOCUMENTS" | "SIGNATURE_REQUESTS" | "EMAILS", outputIndex: number) {

    if (outputGroup === "EMAILS") {
      const ok = emailEditorRef.current?.resetInitialEmail(currentItem?.outputEmails[outputIndex]!, () => setCurrentItemOutput("EMAILS", outputIndex));
      if (emailEditorRef.current && !ok)
        return;
    }

    if (outputGroup === "SIGNATURE_REQUESTS") {
      const ok = emailEditorRef.current?.resetInitialEmail(currentItem?.signatureRequests?.[outputIndex]!, () => setCurrentItemOutput("SIGNATURE_REQUESTS", outputIndex));
      if (emailEditorRef.current && !ok)
        return;
    }

    setCurrentOutputGroup(outputGroup);
    setCurrentOutputIndex(outputIndex);
  }

  async function handleApprove() {
    setCurrentState("APPROVING");
    await postData(`/workflows/automation-items/${itemId}/approve/`)
      .then(mutate)
      .catch(console.error);

    setTimeout(moveToNextItem, 1000);
  }


  async function handleUpdateEmail(email: AbstractEmail): Promise<AbstractEmail> {
    const updatedEmail = await putData(`/workflows/output-emails/${currentOutputEmail!.id}/`, email);

    let outputEmails = [...currentItem?.outputEmails!]
    outputEmails[currentOutputIndex] = updatedEmail;

    mutate({ ...currentItem!, outputEmails })

    return email;
  }

  async function updateEmailAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/workflows/output-emails/${currentOutputEmail!.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/workflows/output-emails-attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/workflows/output-emails-attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  async function updateSender(teamUser: TeamUser) {
    return putData(`/workflows/output-emails/${currentOutputEmail!.id}/sender/`, { senderId: teamUser.id });
  }

  async function handleSendEmail() {
    const updatedEmail = await postData(`/workflows/output-emails/${currentOutputEmail!.id}/send/`);

    let outputEmails = [...currentItem?.outputEmails!]
    outputEmails[currentOutputIndex] = updatedEmail;

    mutate({ ...currentItem!, outputEmails });
  }

  async function updateSignatureRequestAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/signatures/signature-requests/${currentSignatureRequest!.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/signatures/signature-request-attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/signatures/signature-request-attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  async function handleUpdateSignatureRequest(email: AbstractEmail) {
    const updatedEmail = await putData(`/signatures/signature-requests/${currentSignatureRequest!.id}/`, email);

    let signatureRequests = [...currentItem?.signatureRequests!]
    signatureRequests[currentOutputIndex + 1] = updatedEmail;

    mutate({ ...currentItem!, signatureRequests })

    return updatedEmail;
  }

  async function handleRejectItem() {
    const requestChangesUser = currentItem?.initiatingUser?.id;

    setCurrentState("DELETING");
    await postData(`/workflows/automation-items/${itemId}/reject/`, { requestChangesMessage, requestChangesUser })
      .then(mutate)
      .catch(console.error);

    setTimeout(moveToNextItem, 1000);
  }

  function moveToNextItem() {
    mutate();
    setCurrentState("INITIAL");
    setCurrentOutputIndex(0);

    const otherRevieableItems = reviewItems?.filter(item => isReviewable(item) && item.id !== currentItem?.id) ?? [];
    const nextItem = otherRevieableItems[0];

    if (nextItem)
      history.push(url + "/review?" + new URLSearchParams({ item: nextItem.id }).toString());
    else
      history.push(url + "/outputs"); // return to outputs page
  }

  async function sendSignatureRequestSequence() {
    setIsSendingSequence(true);

    const firstSignatureRequest = sendableSignatureRequets?.[0];
    const updatedSignatureRequest = await postData(`/signatures/signature-requests/${firstSignatureRequest!.id}/send/`);

    let signatureRequests = [...currentItem?.signatureRequests!]
    signatureRequests[currentOutputIndex + 1] = updatedSignatureRequest;

    mutate({ ...currentItem!, signatureRequests })

    setIsSendingSequence(false);
  }

  if (!location.pathname.endsWith("/review"))
    return null;

  if (!currentItem || !user || !workflow)
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className='w-24 h-24'>
          <img src="https://static.portant.co/portant-loading-white.svg" alt="Loading Logo" />
        </div>
      </div>
    );

  const currentOutput = currentOutputGroup === "DOCUMENTS" ? currentItem.outputs[currentOutputIndex] : undefined;
  const currentOutputEmail = currentOutputGroup === "EMAILS" ? currentItem.outputEmails[currentOutputIndex] : undefined;
  const currentSignatureRequest = currentOutputGroup === "SIGNATURE_REQUESTS" ? currentItem.signatureRequests?.[currentOutputIndex] : undefined;

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/review")}>
      <TransitionWorkflowPageSideBar>
        {!showRequestChanges &&
          <div className={`flex flex-col gap-1 h-full ${showAutomationPanel && "blur-sm"}`}>
            <div className="flex items-center justify-between font-semibold font-gilroy text-gray-600">
              {t("Documents within this item")} ({currentItem.outputs.length})
            </div>

            {currentItem.outputs.map((output, index) =>
              <div key={output.id} className="flex items-center gap-1 w-full ">
                <div className="flex items-center gap-1 w-full">
                  <img className="h-4 w-4 flex-shrink-0" src={MIMETYPE_ICON_MAP[output.onedriveFile?.fileMimeType ?? output.file?.mimeType ?? output.filePdf?.mimeType ?? ""]} alt="File Icon" />
                  <div className={`font-gilroy truncate w-full ${output.id === currentOutput?.id ? "text-blue font-semibold" : "text-gray-600 hover:font-semibold"} cursor-pointer`} onClick={() => setCurrentItemOutput("DOCUMENTS", index)}>
                    {output.onedriveFile?.fileName ?? output.file?.name ?? output.filePdf?.name}
                  </div>
                </div>
              </div>
            )}

            {(linkableSignatureRequets?.length ?? 0) > 0 &&
              <>
                <div className="flex items-center font-semibold font-gilroy text-gray-600 mt-4">
                  <div className="mr-auto">{t("Signable Document Links")}</div>
                </div>

                {currentItem.signatureRequests?.map((signatureRequest, index) =>
                  <>
                    {!isSendable(signatureRequest) && // NOTE: this check is required as we want to mantain index
                      <div key={signatureRequest.id} className="flex items-center gap-1 w-full ">
                        <div className="flex items-center gap-1 w-full">
                          <img className="h-4 w-4 flex-shrink-0" src={BLOCK_ICON_MAP["SIGNATURE_LINK"]} alt="File Icon" />
                          <a className={`flex items-center gap-0.5 font-gilroy text-gray-600 hover:underline`} href={signatureRequest.publicTokenUrl} target="_blank" rel="noreferrer">
                            <div className="truncate w-[200px]">{signatureRequest.publicTokenUrl}</div>
                            <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" />
                          </a>
                          <CopyToClipboard text={signatureRequest.publicTokenUrl} onCopy={() => handleOnCopiedSignatureRequest(signatureRequest.id)}>
                            {copiedSignatureRequest === signatureRequest.id
                              ? <div className="flex items-center gap-1 text-xs text-blue ml-auto font-semibold select-none"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copied")}</div>
                              : <div className="flex items-center gap-1 text-xs text-gray-600 ml-auto hover:underline cursor-pointer"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copy")}</div>
                            }
                          </CopyToClipboard>
                        </div>
                      </div>
                    }
                  </>
                )}

                {sendableSignatureRequets?.[0].status === "PENDING" &&
                  <div className="flex flex-col gap-1">
                    <button className="btn btn-blue mt-2 text-sm" disabled={isSendingSequence} onClick={() => sendSignatureRequestSequence()}>
                      {isSendingSequence ? t("Sending...") : t("Send sequence")}
                    </button>
                    <div className="text-xs font-semibold text-gray-400">
                      {t("This will send the first email now and enable the following emails to be sent in sequence once the prior request is completed.")}
                    </div>
                  </div>
                }
              </>
            }

            {(sendableSignatureRequets?.length ?? 0) > 0 &&
              <>
                <div className="flex items-center font-semibold font-gilroy text-gray-600 mt-4">
                  <div className="mr-auto">{t("Signature Requests")} ({sendableSignatureRequets?.length})</div>
                  {sendableSignatureRequets?.[0].status !== "PENDING"
                    ? <div className="bg-green w-2 h-2 rounded-full" />
                    : <div className="bg-gray-400 w-2 h-2 rounded-full" />
                  }
                  <div className="text-sm ml-1">Auto-Send</div>
                </div>

                {currentItem.signatureRequests?.map((signatureRequest, index) =>
                  <>
                    {isSendable(signatureRequest) && // NOTE: this check is required as we want to mantain index
                      <div key={signatureRequest.id} className="flex items-center gap-1 w-full ">
                        <div className="flex items-center gap-1 w-full">
                          <img className="h-4 w-4 flex-shrink-0" src={BLOCK_ICON_MAP["SIGNATURE_REQUEST"]} alt="File Icon" />
                          <div className={`font-gilroy truncate w-full ${signatureRequest.id === currentSignatureRequest?.id ? "text-blue font-semibold" : "text-gray-600 hover:font-semibold"} cursor-pointer`} onClick={() => setCurrentItemOutput("SIGNATURE_REQUESTS", index)}>
                            {signatureRequest.recipientEmail}
                          </div>
                          {signatureRequest.status === "PENDING" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-gray-600 border-gray-600 bg-gray-600/10">
                              {t("Draft")}
                            </div>
                          }
                          {signatureRequest.status === "REQUESTED" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-blue border-blue bg-blue/10">
                              {t("Requested")}
                            </div>
                          }
                          {signatureRequest.status === "ACCESSED" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-purple border-purple bg-purple/10">
                              {t("Accessed")}
                            </div>
                          }
                          {signatureRequest.status === "COMPLETED" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-green border-green bg-green/10">
                              {t("Completed")}
                            </div>
                          }
                          {signatureRequest.status === "ERROR" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-red border-red bg-red/10">
                              {t("Error")}
                            </div>
                          }
                          {signatureRequest.status === "CANCELED" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-yellow border-yellow bg-yellow/10">
                              {t("Canceled")}
                            </div>
                          }
                          {signatureRequest.status === "EXPIRED" &&
                            <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-yellow border-yellow bg-yellow/10">
                              {t("Expired")}
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </>
                )}

                {sendableSignatureRequets?.[0].status === "PENDING" &&
                  <div className="flex flex-col gap-1">
                    <button className="btn btn-blue mt-2 text-sm" disabled={isSendingSequence} onClick={() => sendSignatureRequestSequence()}>
                      {isSendingSequence ? t("Sending...") : t("Send sequence")}
                    </button>
                    <div className="text-xs font-semibold text-gray-400">
                      {t("This will send the first email now and enable the following emails to be sent in sequence once the prior request is completed.")}
                    </div>
                  </div>
                }
              </>
            }

            {currentItem.status === "COMPLETED" &&
              <>
                <div className="flex items-center justify-between font-semibold font-gilroy text-gray-600 mt-4">
                  {t("Emails within this item")} ({currentItem.outputEmails.length})
                </div>

                {currentItem.outputEmails.map((outputEmail, index) =>
                  <div key={outputEmail.id} className="flex items-center gap-1 w-full ">
                    <div className="flex items-center gap-1 w-full">
                      <img className="h-4 w-4 flex-shrink-0" src={BLOCK_ICON_MAP[outputEmail.emailType]} alt="File Icon" />
                      <div className={`font-gilroy truncate w-full ${outputEmail.id === currentOutputEmail?.id ? "text-blue font-semibold" : "text-gray-600 hover:font-semibold"} cursor-pointer`} onClick={() => setCurrentItemOutput("EMAILS", index)}>
                        {outputEmail.emailSubject}
                      </div>
                      {outputEmail.status === "PENDING" &&
                        <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-gray-600 border-gray-600 bg-gray-600/10">
                          {t("Draft")}
                        </div>
                      }
                      {outputEmail.status === "SENT" &&
                        <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-green border-green bg-green/10">
                          {t("Sent")}
                        </div>
                      }
                      {outputEmail.status === "FAILED" &&
                        <div className="w-32 text-center text-sm px-4 font-semibold rounded-full cursor-pointer border text-red border-red bg-red/10">
                          {t("Failed")}
                        </div>
                      }
                    </div>
                  </div>
                )}
              </>
            }

            {approversList.length > 0 &&
              <>
                <div className="mt-4 text-sm font-semibold text-gray-600">Approvers</div>
                <div className="flex flex-col gap-2">
                  {approversList.map((ar) => (
                    <div key={ar.id} className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
                      <img className="h-5 w-5 rounded-full border-2 drop-shadow overflow-hidden" src={ar.approver.avatar} alt={"User Avatar"} />
                      <div>{ar.approver.fullName}</div>
                      <div className={`ml-auto mr-4 w-fit px-4 font-semibold rounded-full cursor-pointer border ${(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[1]}`}>
                        {(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[0]}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            }

            {currentItem.approvalRequests.length > 0 &&
              <div className="flex flex-col my-4 gap-1">
                <div className="text-sm font-semibold text-gray-600 ">Activity</div>
                <div className="flex flex-col-reverse gap-2">
                  {currentItem.approvalRequests.filter(ar => ar.status !== "PENDING").map((ar) => (
                    <div key={ar.id} className="flex flex-col">
                      <div className="text-xs text-gray-400">{moment(ar.respondedAt ?? ar.requestedAt).format("DD MMMM, h:mma")}</div>
                      <div className="flex items-center gap-2 font-semibold text-gray-600 text-sm">
                        <img className="h-5 w-5 rounded-full border-2 drop-shadow overflow-hidden" src={ar.approver.avatar} alt={"User Avatar"} />
                        <div>{ar.approver.fullName}</div>
                        <div className={`ml-auto mr-4 w-fit px-4 font-semibold rounded-full cursor-pointer border ${(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[1]}`}>
                          {(ar.isChangeRequest ? CHANGE_REQUEST_STATUS_MAP : APPROVAL_STATUS_MAP)[ar.status]?.[0]}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
            {canApprove &&
              <>
                <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
                <div className="flex flex-col items-center gap-2 mt-4">
                  {currentState === "INITIAL" &&
                    <button className="btn btn-green text-sm h-10 w-full" onClick={handleApprove}>
                      {latestApprovalRequest?.isChangeRequest ? t("Submit changes") : t("Approve")}
                    </button>
                  }
                  {currentState === "APPROVING" &&
                    <button className="btn btn-blue text-sm h-10 w-full" disabled>
                      {t("Processing...")}
                    </button>
                  }
                  {currentState === "APPROVED" &&
                    <button className="btn btn-green text-sm h-10 w-full" disabled>
                      {t("Approved")}
                    </button>
                  }
                  {/* Request Changes button */}
                  {(currentState === "INITIAL" && !latestApprovalRequest?.isChangeRequest && latestApprovalRequest?.approver.id !== currentItem.initiatingUser?.id && currentItem.approvalRequests.length > 0) &&
                    <button className="btn btn-gray text-sm h-10 w-full" onClick={() => setShowRequestChanges(true)}>
                      {t("Request changes")}
                    </button>
                  }
                </div>
              </>
            }
          </div>
        }

        {!!showRequestChanges &&
          <div className={`flex flex-col gap-1 h-full ${showAutomationPanel && "blur-sm"}`}>
            <div className="flex items-center justify-between font-semibold font-gilroy text-gray-600">
              {t("Request Changes")}
            </div>

            <div className="text-xs">
              {t("Request any changes to be made. Add comments directly in the document for detailed feedback.")}
            </div>

            <div className="h-px w-full bg-gray-300 flex-shrink-0 my-2" />

            <div className="text-xs font-semibold text-gray-600 mt-4">
              Change request to be sent to:
            </div>
            {currentItem.initiatingUser &&
              <div className="flex items-center px-2 gap-2">
                <img className="h-8 w-8 rounded-full border-2 drop-shadow overflow-hidden" src={currentItem.initiatingUser.avatar} alt={"User Avatar"} />
                <div className="flex flex-col items-start">
                  <div className="text-sm font-semibold text-gray-600">
                    {currentItem.initiatingUser.fullName}
                  </div>
                  <div className="text-xs text-gray-400">
                    {currentItem.initiatingUser.email}
                  </div>
                </div>
              </div>
            }

            <div className="text-xs font-semibold text-gray-600 mt-4">
              Message
            </div>
            <textarea className="w-full min-h-32 p-2 border border-gray-300 rounded resize-y text-sm" value={requestChangesMessage} placeholder={t("The document(s) requires updates before approval can continue. Please review the suggested edits and resolve the comments.")} onChange={(e) => setRequestChangesMessage(e.currentTarget.value)} />

            {(latestApprovalRequest?.approver.id === user.id) &&
              <>
                <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
                <div className="flex flex-col items-center gap-2 mt-4">
                  {currentState === "INITIAL" &&
                    <button className="btn btn-blue text-sm h-10 w-full" onClick={handleRejectItem}>
                      {t("Submit")}
                    </button>
                  }
                  {currentState === "APPROVING" &&
                    <button className="btn btn-blue text-sm h-10 w-full" disabled>
                      {t("Processing...")}
                    </button>
                  }
                  {currentState === "DELETING" &&
                    <button className="btn btn-red text-sm h-10 w-full" disabled>
                      {t("Submitting...")}
                    </button>
                  }
                  {currentState === "APPROVED" &&
                    <button className="btn btn-green text-sm h-10 w-full" disabled>
                      {t("Submitted")}
                    </button>
                  }
                  {/* Request Changes button */}
                  {currentState === "INITIAL" &&
                    <button className="btn btn-gray text-sm h-10 w-full" onClick={() => setShowRequestChanges(false)}>
                      {t("Back")}
                    </button>
                  }
                </div>
              </>
            }
          </div>
        }
      </TransitionWorkflowPageSideBar>

      {currentOutputGroup === "DOCUMENTS" && currentOutput && currentOutput.onedriveFile &&
        <TransitionWorkflowPageContent>
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[currentOutput.onedriveFile.fileMimeType]} alt="Microsoft Excel File Icon" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col h-full justify-center">
                <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(currentOutput.onedriveFile?.fileUrl, "_blank")}>
                  <div className="truncate max-w-[400px]">
                    {currentOutput.onedriveFile.fileName}
                  </div>
                  {currentOutput.onedriveFile.fileUrl &&
                    <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                  }
                </div>
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
          </div>
          <iframe title={t("Microsoft File Viewer")} height="1000" className="w-full h-full border border-gray-300 rounded overflow-hidden"
            src={currentOutput.onedriveFile.fileEmbedViewUrl} />
        </TransitionWorkflowPageContent>
      }

      {currentOutputGroup === "DOCUMENTS" && currentOutput && !currentOutput.onedriveFile &&
        <TransitionWorkflowPageContent className="relative">
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[currentOutput.file?.mimeType ?? currentOutput.filePdf?.mimeType ?? "application/vnd.google-apps.document"]} alt="Google File Icon" />
            </div>
            <div className="flex flex-col h-full justify-center">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(currentOutput.file?.url ?? currentOutput.filePdf?.url, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {currentOutput.file?.name ?? currentOutput.filePdf?.name}
                </div>
                {(currentOutput.file?.url ?? currentOutput.filePdf?.url) &&
                  <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                }
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url + "/outputs")} />
          </div>

          {(currentOutput.file && currentOutput.file.mimeType !== "application/pdf") &&
            <iframe title={t("Google Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={`${currentOutput.file.url}?widget&rm=embedded`} />
          }
          {(!currentOutput.file && !!currentOutput.filePdf) &&
            <iframe title={t("Google Drive PDF")} src={`https://drive.google.com/file/d/${currentOutput.filePdf.id}/preview`} className="w-full h-full border border-gray-300 rounded overflow-hidden" />
          }
        </TransitionWorkflowPageContent>
      }

      {currentOutputGroup === "EMAILS" && currentOutputEmail &&
        <EmailEditor ref={emailEditorRef} initialEmail={currentOutputEmail} updateEmail={handleUpdateEmail} updateSender={updateSender} updateAttachment={updateEmailAttachment} sendEmail={handleSendEmail} onClose={() => history.push(url + "/outputs")} isSent={currentOutputEmail.status === "SENT"} disabled={currentOutputEmail.status === "SENT" || !hasPermission(workflow.team, "workflows.edit")} />
      }

      {currentOutputGroup === "SIGNATURE_REQUESTS" && currentSignatureRequest &&
        <>
          {["PREVIEW", "LINK", "LINK_UNPUBLISHED"].includes(currentSignatureRequest.status)
            ? <SignatureRequestLinkPreview signatureRequest={currentSignatureRequest} />
            : <EmailEditor ref={emailEditorRef} initialEmail={currentSignatureRequest} updateEmail={handleUpdateSignatureRequest} updateAttachment={updateSignatureRequestAttachment} onClose={() => history.push(url + "/outputs")} disabled={currentSignatureRequest.status !== "PENDING" || !hasPermission(workflow.team, "workflows.edit")} customTo={<>{currentSignatureRequest.emailTo}</>} customBodyComponent={SignatureRequestEmailContant} />
          }
        </>
      }

    </TransitionWorkflowPage>
  );

}

export default WorkflowOutputsDetail;
